/**
 * "Did you mean" for a typed payee name (TMMAPP-0120 item 16). tmm-api reuses a payee only on an
 * exact, case-blind name match (`findOrCreatePayee`), so a typo silently makes a second payee. A
 * typed name is **close** to an existing payee when the two are equal once case, spaces and
 * punctuation are ignored, or are at most two letters apart (edit distance on that folded form).
 * The candidates come from the payees search on the typed name's first word; the comparison runs
 * here, in the browser.
 */

/** Case-blind, trimmed: the match tmm-api itself makes. */
export function sameName(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
}

/** Lower case, with every space and punctuation mark dropped. */
export function foldName(value: string): string {
  return value.toLocaleLowerCase().replace(/[\s\p{P}\p{S}]/gu, '');
}

/** Levenshtein distance, stopping early once it passes `limit`. */
function distance(a: string, b: string, limit: number): number {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    let best = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const value = Math.min(
        (previous[j] ?? 0) + 1,
        (current[j - 1] ?? 0) + 1,
        (previous[j - 1] ?? 0) + cost,
      );
      current.push(value);
      best = Math.min(best, value);
    }
    if (best > limit) return limit + 1;
    previous = current;
  }
  return previous[b.length] ?? limit + 1;
}

export const CLOSE_LETTERS = 2;

/**
 * The closest of `candidates` to `typed`, or null when none is close or one already matches
 * exactly (then there is nothing to suggest: tmm-api reuses that payee).
 */
export function closestName<T extends { name: string }>(
  typed: string,
  candidates: readonly T[],
): T | null {
  const text = typed.trim();
  if (text.length === 0) return null;
  if (candidates.some((c) => sameName(c.name, text))) return null;
  const folded = foldName(text);
  if (folded.length === 0) return null;
  let best: { candidate: T; score: number } | null = null;
  for (const candidate of candidates) {
    const score = distance(folded, foldName(candidate.name), CLOSE_LETTERS);
    if (score > CLOSE_LETTERS) continue;
    if (best === null || score < best.score) best = { candidate, score };
  }
  return best?.candidate ?? null;
}

/** The first word of a typed name, when it is long enough to search on (3+ characters). */
export function searchWord(typed: string): string | null {
  const first = typed.trim().split(/\s+/)[0] ?? '';
  return first.length >= 3 ? first : null;
}
